/**
 * The only module that touches Dexie. UI and domain code go through here.
 */

import { db, DEFAULT_SYNC_META, type SyncMeta } from './db.ts';
import Dexie, { type Transaction as DatabaseTransaction } from 'dexie';
import {
  DEFAULT_SETTINGS,
  type Budget,
  type Card,
  type Category,
  type IssuerId,
  type Person,
  type Rule,
  type Settings,
  type Statement,
  type Tombstone,
  type TombstoneCollection,
  type Transaction,
} from '../domain/types.ts';
import { cleanCell } from '../domain/text.ts';
import {
  findDanglingCards,
  findOrphanedRows,
  findPersonDrift,
} from '../domain/attribution.ts';
import { buildManualTransaction, type ManualEntry } from '../domain/transactions.ts';
import { defaultCategories } from '../domain/categories.ts';
import { defaultRules } from '../categorization/defaults.ts';
import { categorize } from '../categorization/engine.ts';

const localTransactions = new WeakSet<DatabaseTransaction>();
const unstampedTransactions = new WeakSet<DatabaseTransaction>();
const changedTransactions = new WeakSet<DatabaseTransaction>();
function isLocalWrite(tx: DatabaseTransaction): boolean {
  return localTransactions.has(tx) || (tx.parent != null && isLocalWrite(tx.parent));
}
function localTransaction(tx: DatabaseTransaction): DatabaseTransaction | null {
  if (localTransactions.has(tx)) return tx;
  return tx.parent ? localTransaction(tx.parent) : null;
}

// Keep the write and its pending-sync revision in the same IndexedDB commit.
async function localMutation<T>(write: () => Promise<T>, stamp = true): Promise<T> {
  if (Dexie.currentTransaction && isLocalWrite(Dexie.currentTransaction)) return write();
  return db.transaction('rw', db.tables, async (tx) => {
    localTransactions.add(tx);
    if (!stamp) unstampedTransactions.add(tx);
    const result = await write();
    if (changedTransactions.has(tx)) await markDirty();
    return result;
  });
}

for (const table of db.tables.filter((table) => table.name !== 'syncMeta')) {
  const changed = (tx: DatabaseTransaction) => {
    const local = localTransaction(tx);
    if (local) changedTransactions.add(local);
  };
  table.hook('creating', (_key, _row, tx) => { changed(tx); });
  table.hook('updating', (_changes, _key, _row, tx) => { changed(tx); });
  table.hook('deleting', (_key, _row, tx) => { changed(tx); });
}

function nextStamp(previous?: string): string {
  return new Date(Math.max(Date.now(), (Date.parse(previous ?? '') || 0) + 1)).toISOString();
}

for (const table of [db.people, db.cards, db.categories, db.rules, db.budgets]) {
  table.hook('creating', function(_key, row, tx) {
    const local = localTransaction(tx);
    if (local) row.updatedAt = unstampedTransactions.has(local) ? '1970-01-01T00:00:00.000Z' : nextStamp(row.updatedAt);
  });
  table.hook('updating', function(_changes, _key, row, tx) {
    const local = localTransaction(tx);
    if (local && !unstampedTransactions.has(local)) return { updatedAt: nextStamp(row.updatedAt) };
  });
}
db.settings.hook('creating', function(_key, row, tx) {
  if (isLocalWrite(tx)) row.value = { ...row.value, updatedAt: nextStamp(row.value.updatedAt) };
});
db.settings.hook('updating', function(_changes, _key, row, tx) {
  if (isLocalWrite(tx)) {
    return { 'value.updatedAt': nextStamp(row.value.updatedAt) };
  }
});

function randomId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID()}`;
}

/* ------------------------------------------------------------------ people */

export async function listPeople(): Promise<Person[]> {
  return db.people.toArray();
}

export async function createPerson(displayName: string, alias?: string): Promise<Person> {
  return localMutation(async () => {
    const palette = ['#2563eb', '#db2777', '#16a34a', '#d97706', '#7c3aed'];
    const count = await db.people.count();
    const person: Person = {
      id: randomId('person'),
      displayName: displayName.trim(),
      statementAliases: alias ? [cleanCell(alias)] : [],
      color: palette[count % palette.length] as string,
    };
    await db.people.add(person);
    return person;
  });
}

export async function addPersonAlias(personId: string, alias: string): Promise<void> {
  return localMutation(async () => {
    const person = await db.people.get(personId);
    if (!person) return;
    const cleaned = cleanCell(alias);
    if (cleaned.length === 0 || person.statementAliases.includes(cleaned)) return;
    await db.people.update(personId, { statementAliases: [...person.statementAliases, cleaned] });
  });
}

/**
 * Match a statement's cardholder name against known people. Exact match on a
 * recorded alias only — a fuzzy match here would attribute a year of one
 * person's spending to the other, which is close to invisible once it happens.
 */
export async function findPersonByAlias(cardholderName: string | null): Promise<Person | null> {
  if (cardholderName === null) return null;
  const needle = cleanCell(cardholderName);
  if (needle.length === 0) return null;
  const people = await db.people.toArray();
  return people.find((person) => person.statementAliases.includes(needle)) ?? null;
}

/* ------------------------------------------------------------------- cards */

export async function listCards(): Promise<Card[]> {
  return db.cards.toArray();
}

export async function findCard(issuer: IssuerId, last4: string): Promise<Card | null> {
  if (last4.length === 0) return null;
  return (await db.cards.where('[issuer+last4]').equals([issuer, last4]).first()) ?? null;
}

export async function createCard(input: {
  personId: string;
  issuer: IssuerId;
  last4: string;
  nickname: string;
  productName: string | null;
}): Promise<Card> {
  return localMutation(async () => {
    // Imported sources are local unless the user explicitly reclassifies them
    // in Settings. This keeps a newly imported statement visible under the
    // default Israel filter without asking a question during every import.
    const card: Card = { id: randomId('card'), ...input, region: 'israel' };
    await db.cards.add(card);
    return card;
  });
}

/* -------------------------------------------------------------- statements */

export async function listStatements(): Promise<Statement[]> {
  return db.statements.orderBy('importedAt').reverse().toArray();
}

export async function findStatementByHash(fileHash: string): Promise<Statement | null> {
  return (await db.statements.where('fileHash').equals(fileHash).first()) ?? null;
}

/* ------------------------------------------------------------ transactions */

export async function listTransactions(): Promise<Transaction[]> {
  return db.transactions.toArray();
}

export async function countTransactions(): Promise<number> {
  return db.transactions.count();
}

/**
 * Commit an import atomically: either the statement record and all its rows
 * land, or neither does. A half-written import would leave rows that "undo
 * import" could not find.
 */
export async function commitImport(
  statement: Statement,
  transactions: readonly Transaction[],
): Promise<void> {
  return localMutation(async () => {
    await db.transaction('rw', db.statements, db.transactions, async () => {
      await db.statements.put(statement);
      if (transactions.length > 0) {
        await db.transactions.bulkPut(transactions as Transaction[]);
      }
    });
  });
}

export interface UndoResult {
  removed: number;
  /** Rows kept because a human had edited them. */
  keptEdited: number;
}

/**
 * Remove everything a single import added.
 *
 * Rows edited by hand since the import are kept, not deleted — the user's own
 * work outranks an undo they may have half-forgotten about. The caller reports
 * what was kept.
 */
export async function undoImport(
  sourceFileId: string,
  options: { includeEdited?: boolean } = {},
): Promise<UndoResult> {
  return localMutation(async () => {
    const { includeEdited = false } = options;

    const result = await db.transaction('rw', db.statements, db.transactions, async () => {
      const rows = await db.transactions.where('sourceFileId').equals(sourceFileId).toArray();
      const removable = includeEdited ? rows : rows.filter((row) => !row.manuallyEdited);

      await db.transactions.bulkDelete(removable.map((row) => row.id));

      const keptEdited = rows.length - removable.length;
      if (keptEdited === 0) {
        await db.statements.delete(sourceFileId);
      }

      return { removedIds: removable.map((row) => row.id), keptEdited };
    });

    // Without these, undoing an import here and syncing would pull every row
    // straight back from the other device's copy.
    await recordTombstones('transactions', result.removedIds);
    if (result.keptEdited === 0) await recordTombstones('statements', [sourceFileId]);


    return { removed: result.removedIds.length, keptEdited: result.keptEdited };
  });
}

/**
 * Add a transaction by hand.
 *
 * Not part of an import, so there is no statement and nothing to reconcile
 * against: the row is written as given. The id comes from a fresh uid, which is
 * what keeps a hand-entered row clear of any tombstone left by an imported one.
 */
export async function createManualTransaction(entry: ManualEntry): Promise<Transaction> {
  return localMutation(async () => {
    const transaction = buildManualTransaction(entry, {
      uid: crypto.randomUUID(),
      now: new Date().toISOString(),
    });
    await db.transactions.add(transaction);

    return transaction;
  });
}

export async function updateTransaction(
  id: string,
  changes: Partial<Transaction>,
): Promise<void> {
  return localMutation(async () => {
    await db.transactions.update(id, {
      ...changes,
      updatedAt: new Date().toISOString(),
      manuallyEdited: true,
    });
  });
}

/* ---------------------------------------------------------------- sync meta */

export async function getSyncMeta(): Promise<SyncMeta> {
  const row = await db.syncMeta.get('sync');
  return { ...DEFAULT_SYNC_META, ...(row?.value ?? {}) };
}

export async function saveSyncMeta(meta: SyncMeta): Promise<void> {
  await db.syncMeta.put({ key: 'sync', value: meta });
}

type DirtyListener = () => void;
const dirtyListeners = new Set<DirtyListener>();

export function onDirtyChange(listener: DirtyListener): () => void {
  dirtyListeners.add(listener);
  return () => {
    dirtyListeners.delete(listener);
  };
}

/** Records that local data has diverged from OneDrive and notifies listeners for auto-sync. */
export async function markDirty(): Promise<void> {
  await db.transaction('rw', db.syncMeta, async () => {
    const meta = await getSyncMeta();
    await saveSyncMeta({ ...meta, dirty: true, revision: meta.revision + 1 });
    // Notify only after commit, including when called inside a larger write.
    let tx = Dexie.currentTransaction!;
    while (tx.parent) tx = tx.parent;
    tx.on('complete', () => {
      for (const listener of dirtyListeners) {
        try { listener(); } catch { /* A listener cannot undo the committed write. */ }
      }
    });
  });
}

/** Do not acknowledge writes made while the network upload was in progress. */
export async function acknowledgeSync(meta: SyncMeta, uploadedRevision: number): Promise<void> {
  await db.transaction('rw', db.syncMeta, async () => {
    const current = await getSyncMeta();
    await saveSyncMeta({ ...meta, revision: current.revision, dirty: current.revision !== uploadedRevision });
  });
}

/* ----------------------------------------------------------- tombstones */

export async function listTombstones(): Promise<Tombstone[]> {
  return db.tombstones.toArray();
}

/**
 * Record that rows were deleted, so the deletion survives the next sync merge.
 *
 * Every delete path in this module calls this. A path that forgets to is a
 * row that comes back from OneDrive — which is the bug this exists to fix, so
 * the call belongs next to the `delete` itself rather than in the caller.
 *
 * Does not mark dirty on its own: it is always paired with a deletion that
 * does, and marking twice wakes the auto-sync listener for no reason.
 */
export async function recordTombstones(
  collection: TombstoneCollection,
  ids: readonly string[],
): Promise<void> {
  if (ids.length === 0) return;
  const deletedAt = new Date().toISOString();
  await db.tombstones.bulkPut(ids.map((id) => ({ collection, id, deletedAt })));
}

/** Replace the local tombstone set with the merged, pruned one from a sync. */
export async function replaceTombstones(tombstones: readonly Tombstone[]): Promise<void> {
  await db.transaction('rw', db.tombstones, async () => {
    await db.tombstones.clear();
    if (tombstones.length > 0) await db.tombstones.bulkPut([...tombstones]);
  });
}

/* ------------------------------------------------------- config collections */

export async function listCategories(): Promise<Category[]> {
  return db.categories.toArray();
}

export async function listRules(): Promise<Rule[]> {
  return db.rules.toArray();
}

export async function listBudgets(): Promise<Budget[]> {
  return db.budgets.toArray();
}

/**
 * Replace local state with the merged result of a sync, in one transaction.
 *
 * Replacement, tombstones and cleanup run under the sync transaction, so
 * local writers cannot interleave with a snapshot being applied.
 */
export async function applyMerged(input: {
  transactions: Transaction[];
  statements: Statement[];
  people: Person[];
  cards: Card[];
  categories: Category[];
  rules: Rule[];
  budgets: Budget[];
  settings: Settings;
}): Promise<void> {
  await db.transaction(
    'rw',
    db.tables,
    async () => {
      await Promise.all([
        db.transactions.clear().then(() => db.transactions.bulkPut(input.transactions)),
        db.statements.clear().then(() => db.statements.bulkPut(input.statements)),
        db.people.clear().then(() => db.people.bulkPut(input.people)),
        db.cards.clear().then(() => db.cards.bulkPut(input.cards)),
        db.categories.clear().then(() => db.categories.bulkPut(input.categories)),
        db.rules.clear().then(() => db.rules.bulkPut(input.rules)),
        db.budgets.clear().then(() => db.budgets.bulkPut(input.budgets)),
        db.settings.put({ key: 'settings', value: input.settings }),
      ]);
    },
  );
  await cleanupDuplicateAndOrphanedTransactions({ markDirtyOnChange: false });
  /*
   * A person tombstone from another device arrives on its own: it deletes the
   * person here while every row that named them stays behind, unselectable. The
   * rows' cards still know the answer, so attribution is recomputed after each
   * merge rather than assumed to have survived it.
   */
  await reconcilePersonAttribution({ markDirtyOnChange: false });
}

/* ---------------------------------------------------------------- settings */

export async function getSettings(): Promise<Settings> {
  const row = await db.settings.get('settings');
  return row?.value ?? DEFAULT_SETTINGS;
}

export async function saveSettings(settings: Settings): Promise<void> {
  return localMutation(async () => {
    await db.settings.put({ key: 'settings', value: settings });
  });
}

/* ------------------------------------------------------------------- admin */

export async function clearAllData(): Promise<void> {
  return localMutation(async () => {
    await db.transaction(
      'rw',
      [db.transactions, db.statements, db.people, db.cards, db.categories, db.rules, db.budgets, db.settings, db.tombstones],
      async () => {
        await Promise.all([
          db.transactions.clear(),
          db.statements.clear(),
          db.people.clear(),
          db.cards.clear(),
          db.categories.clear(),
          db.rules.clear(),
          db.budgets.clear(),
          db.settings.clear(),
          /*
           * Cleared, not converted into tombstones. "Start over" is a local
           * reset; turning it into a deletion record would propagate the wipe to
           * every other device on the next sync, which is not what anyone means
           * by clearing this browser.
           */
          db.tombstones.clear(),
        ]);
      },
    );
  });
}

export { randomId };


/* -------------------------------------------------------------- seeding */

/**
 * Populate the default category tree and rule set on first run.
 *
 * Only seeds when BOTH tables are empty. Someone who deleted every category on
 * purpose should not have them silently reappear on next load, and someone
 * mid-sync should not get defaults merged into a curated tree.
 */
export async function seedDefaultsIfEmpty(): Promise<boolean> {
  return localMutation(async () => {
    const [categoryCount, ruleCount] = await Promise.all([
      db.categories.count(),
      db.rules.count(),
    ]);
    if (categoryCount > 0 || ruleCount > 0) return false;

    await db.transaction('rw', [db.categories, db.rules], async () => {
      await db.categories.bulkPut(defaultCategories());
      await db.rules.bulkPut(defaultRules());
    });

    // Categorize whatever is already stored. Transactions imported before the
    // rules existed would otherwise sit uncategorized forever, which makes
    // shipping a default rule set pointless on any existing dataset.
    await applyRules({ onlyUncategorized: true });

    return true;
  }, false);
}

/* ----------------------------------------------------------- categories */

export async function saveCategory(category: Category): Promise<void> {
  return localMutation(async () => {
    await db.categories.put(category);
  });
}

/**
 * Delete a category, moving anything filed under it somewhere safe.
 *
 * Transactions are reassigned to `moveToId` (or left uncategorized), and child
 * categories are re-parented rather than orphaned — an orphaned child would
 * vanish from the tree while its transactions kept pointing at it.
 */
export async function deleteCategory(id: string, moveToId: string | null): Promise<number> {
  return localMutation(async () => {
    /** Rules removed along with the category, so they can be tombstoned too. */
    let deletedRuleIds: string[] = [];

    const moved = await db.transaction(
      'rw',
      [db.categories, db.transactions, db.rules],
      async () => {
        const children = await db.categories.where('parentId').equals(id).toArray();
        const target = moveToId === null ? null : await db.categories.get(moveToId);
        await Promise.all(
          children.map((child) =>
            db.categories.update(child.id, { parentId: target?.parentId ?? null }),
          ),
        );

        const affected = await db.transactions.filter(
          (tx) => tx.categoryId === id || tx.subcategoryId === id,
        ).toArray();

        await db.transactions.bulkPut(
          affected.map((tx) => ({
            ...tx,
            categoryId: tx.categoryId === id ? moveToId : tx.categoryId,
            subcategoryId: tx.subcategoryId === id ? null : tx.subcategoryId,
            updatedAt: new Date().toISOString(),
          })),
        );

        // Rules pointing at a deleted category would silently stop matching.
        const rules = await db.rules.toArray();
        const stale = rules.filter(
          (rule) => rule.assign.categoryId === id || rule.assign.subcategoryId === id,
        );
        if (moveToId === null) {
          await db.rules.bulkDelete(stale.map((rule) => rule.id));
          deletedRuleIds = stale.map((rule) => rule.id);
        } else {
          await db.rules.bulkPut(
            stale.map((rule) => ({
              ...rule,
              assign: {
                ...rule.assign,
                categoryId: rule.assign.categoryId === id ? moveToId : rule.assign.categoryId,
                ...(rule.assign.subcategoryId === id ? { subcategoryId: undefined } : {}),
              },
            })),
          );
        }

        await db.categories.delete(id);
        return affected.length;
      },
    );

    await recordTombstones('categories', [id]);
    await recordTombstones('rules', deletedRuleIds);

    return moved;
  });
}

/* ---------------------------------------------------------------- rules */

export async function saveRule(rule: Rule): Promise<void> {
  return localMutation(async () => {
    await db.rules.put(rule);
  });
}

/**
 * Write several rules at once — reordering renumbers the list, so it touches
 * more than the rule that moved. One transaction and one dirty mark, otherwise
 * a half-applied reorder could leave two rules sharing a position.
 */
export async function saveRules(rules: readonly Rule[]): Promise<void> {
  return localMutation(async () => {
    if (rules.length === 0) return;
    await db.rules.bulkPut([...rules]);
  });
}

export async function deleteRule(id: string): Promise<void> {
  return localMutation(async () => {
    await db.rules.delete(id);
    await recordTombstones('rules', [id]);
  });
}

/* ------------------------------------------------- applying categories */

/**
 * Assign a category to specific transactions.
 *
 * `markManual` defaults to true, because a person choosing a category is
 * exactly the signal that rules must not override it later. Undo passes false:
 * putting a value back the way it was is not a new edit, and marking it as one
 * would leave the row permanently flagged as hand-touched — which also blocks
 * "undo import" from ever removing it.
 */
export async function assignCategory(
  transactionIds: readonly string[],
  categoryId: string | null,
  subcategoryId: string | null = null,
  options: { markManual?: boolean } = {},
): Promise<number> {
  return localMutation(async () => {
    const { markManual = true } = options;
    const now = new Date().toISOString();
    const updated = await db.transaction('rw', db.transactions, async () => {
      const rows = await db.transactions.bulkGet([...transactionIds]);
      const present = rows.filter((row): row is Transaction => row !== undefined);
      await db.transactions.bulkPut(
        present.map((row) => ({
          ...row,
          categoryId,
          subcategoryId,
          updatedAt: now,
          manuallyEdited: markManual ? true : row.manuallyEdited,
        })),
      );
      return present.length;
    });

    return updated;
  });
}

export interface CategorySnapshot {
  id: string;
  categoryId: string | null;
  subcategoryId: string | null;
  manuallyEdited: boolean;
}

/**
 * Put transactions back exactly as they were, including the manuallyEdited
 * flag. Used by undo, which must restore state rather than write new state.
 */
export async function restoreCategories(snapshots: readonly CategorySnapshot[]): Promise<void> {
  return localMutation(async () => {
    const now = new Date().toISOString();
    await db.transaction('rw', db.transactions, async () => {
      const rows = await db.transactions.bulkGet(snapshots.map((entry) => entry.id));
      const updates: Transaction[] = [];
      rows.forEach((row, index) => {
        const snapshot = snapshots[index];
        if (row === undefined || snapshot === undefined) return;
        updates.push({
          ...row,
          categoryId: snapshot.categoryId,
          subcategoryId: snapshot.subcategoryId,
          manuallyEdited: snapshot.manuallyEdited,
          updatedAt: now,
        });
      });
      if (updates.length > 0) await db.transactions.bulkPut(updates);
    });
  });
}

/**
 * Permanently remove specific transactions.
 *
 * The deliberate escape hatch from "undo import keeps hand-edited rows": without
 * a direct delete, a row that was ever touched could never be removed at all.
 */
export async function deleteTransactions(transactionIds: readonly string[]): Promise<number> {
  return localMutation(async () => {
    const removed = await db.transaction('rw', db.transactions, async () => {
      await db.transactions.bulkDelete([...transactionIds]);
      return transactionIds.length;
    });
    await recordTombstones('transactions', transactionIds);

    return removed;
  });
}

/**
 * Set a row's free-text note.
 *
 * `notes` is the reader's own field. The issuer's `extraDetails` is never
 * touched: it is the statement's own words, kept verbatim so a number can
 * always be traced back to the file it came from, and a note that overwrote it
 * would destroy the only audit trail a row has.
 *
 * Marks the row hand-edited, like every other manual change. That is what stops
 * "undo import" from silently deleting a transaction someone took the trouble
 * to annotate.
 */
export async function setNotes(transactionId: string, notes: string | null): Promise<boolean> {
  return localMutation(async () => {
    const trimmed = notes === null ? null : notes.trim();
    const value = trimmed !== null && trimmed.length > 0 ? trimmed : null;

    const changed = await db.transaction('rw', db.transactions, async () => {
      const row = await db.transactions.get(transactionId);
      // A blur with nothing typed is not an edit, and writing anyway would bump
      // `updatedAt` — which is what decides a two-device merge — for no reason.
      if (row === undefined || row.notes === value) return false;
      await db.transactions.put({
        ...row,
        notes: value,
        updatedAt: new Date().toISOString(),
        manuallyEdited: true,
      });
      return true;
    });


    return changed;
  });
}

export async function setExcluded(
  transactionIds: readonly string[],
  excluded: boolean,
): Promise<number> {
  return localMutation(async () => {
    const now = new Date().toISOString();
    const updated = await db.transaction('rw', db.transactions, async () => {
      const rows = await db.transactions.bulkGet([...transactionIds]);
      const present = rows.filter((row): row is Transaction => row !== undefined);
      await db.transactions.bulkPut(
        present.map((row) => ({
          ...row,
          excludedFromReports: excluded,
          updatedAt: now,
          manuallyEdited: true,
        })),
      );
      return present.length;
    });

    return updated;
  });
}

export interface ApplyRulesResult {
  categorized: number;
  stillUncategorized: number;
}

/**
 * Run the rule set over stored transactions.
 *
 * `respectManual` defaults to true, so re-running rules never overwrites a
 * category a human chose. `onlyUncategorized` limits it to rows nothing has
 * touched, which is what an import should do.
 */
export async function applyRules(
  options: { respectManual?: boolean; onlyUncategorized?: boolean } = {},
): Promise<ApplyRulesResult> {
  return localMutation(async () => {
    const { respectManual = true, onlyUncategorized = false } = options;

    const [rules, all] = await Promise.all([db.rules.toArray(), db.transactions.toArray()]);
    const candidates = onlyUncategorized ? all.filter((tx) => tx.categoryId === null) : all;

    const now = new Date().toISOString();
    const updates: Transaction[] = [];

    for (const tx of candidates) {
      if (respectManual && tx.manuallyEdited && tx.categoryId !== null) continue;
      const assignment = categorize(tx, rules);
      if (assignment === null) continue;
      if (tx.categoryId === assignment.categoryId && tx.subcategoryId === assignment.subcategoryId) {
        continue;
      }
      updates.push({
        ...tx,
        categoryId: assignment.categoryId,
        subcategoryId: assignment.subcategoryId,
        tags: assignment.tags.length > 0 ? [...new Set([...tx.tags, ...assignment.tags])] : tx.tags,
        updatedAt: now,
        // Rule-applied categories are not manual edits; a later rule change may
        // legitimately revise them.
        manuallyEdited: tx.manuallyEdited,
      });
    }

    if (updates.length > 0) {
      await db.transactions.bulkPut(updates);

    }

    const stillUncategorized = await db.transactions.filter((tx) => tx.categoryId === null).count();
    return { categorized: updates.length, stillUncategorized };
  });
}

/** How many stored transactions a prospective rule would match. */
export async function countMatching(rule: Rule): Promise<number> {
  const all = await db.transactions.toArray();
  return all.filter((tx) => categorize(tx, [rule]) !== null).length;
}

/* ------------------------------------------------------------- people */

export async function savePerson(person: Person): Promise<void> {
  return localMutation(async () => {
    await db.people.put(person);
  });
}

/**
 * Delete a person, moving everything they own to `reassignToPersonId` first.
 *
 * Refuses while they still own cards *or* transactions. Guarding on cards alone
 * was not enough: a person can end up owning history with no card — reassign the
 * card, and the rows stay behind — and deleting them then stranded that history
 * under an id nothing could select again.
 */
export async function deletePerson(
  id: string,
  options?: { reassignToPersonId?: string },
): Promise<{ deleted: boolean; cardCount: number; transactionCount: number }> {
  return localMutation(async () => {
    const [cardCount, transactionCount] = await Promise.all([
      db.cards.where('personId').equals(id).count(),
      db.transactions.where('personId').equals(id).count(),
    ]);

    if (options?.reassignToPersonId !== undefined && options.reassignToPersonId !== id) {
      const targetPerson = await db.people.get(options.reassignToPersonId);
      if (targetPerson) {
        const now = new Date().toISOString();
        const [cards, txs, stmts] = await Promise.all([
          db.cards.where('personId').equals(id).toArray(),
          db.transactions.where('personId').equals(id).toArray(),
          db.statements.where('personId').equals(id).toArray(),
        ]);
        await db.transaction(
          'rw',
          [db.people, db.cards, db.transactions, db.statements],
          async () => {
            await db.cards.bulkPut(cards.map((c) => ({ ...c, personId: targetPerson.id })));
            await db.transactions.bulkPut(
              txs.map((tx) => ({ ...tx, personId: targetPerson.id, updatedAt: now })),
            );
            await db.statements.bulkPut(stmts.map((s) => ({ ...s, personId: targetPerson.id })));
            await db.people.delete(id);
          },
        );
        await recordTombstones('people', [id]);

        return { deleted: true, cardCount: 0, transactionCount: 0 };
      }
    }

    if (cardCount > 0 || transactionCount > 0) {
      return { deleted: false, cardCount, transactionCount };
    }
    await db.people.delete(id);
    await recordTombstones('people', [id]);

    return { deleted: true, cardCount: 0, transactionCount: 0 };
  });
}

/**
 * Save a card, carrying its rows with it when the owner changes.
 *
 * The card owns the person relationship, so a reassignment has to reach the
 * card's transactions and statements in the same breath. Writing only the card
 * left every row claiming the previous owner, which is invisible until a
 * per-person total is read.
 */
export async function saveCard(card: Card): Promise<void> {
  return localMutation(async () => {
    const previous = await db.cards.get(card.id);
    const ownerChanged = previous !== undefined && previous.personId !== card.personId;

    if (ownerChanged) {
      const now = new Date().toISOString();
      const [txs, stmts] = await Promise.all([
        db.transactions.where('cardId').equals(card.id).toArray(),
        db.statements.where('cardId').equals(card.id).toArray(),
      ]);
      await db.transaction('rw', [db.cards, db.transactions, db.statements], async () => {
        await db.cards.put(card);
        await db.transactions.bulkPut(
          txs.map((tx) => ({ ...tx, personId: card.personId, updatedAt: now })),
        );
        await db.statements.bulkPut(stmts.map((s) => ({ ...s, personId: card.personId })));
      });
    } else {
      await db.cards.put(card);
    }
  });
}

export interface AttributionRepair {
  /** Transactions whose personId was corrected from their card. */
  transactions: number;
  /** Statements likewise. */
  statements: number;
  /** Rows left alone because their card is gone — see `findOrphanedRows`. */
  orphaned: number;
  /** Cards pointing at a person who no longer exists. Needs a human. */
  danglingCards: Card[];
}

/**
 * Re-derive every row's `personId` from its card.
 *
 * The repair for drift already on disk, and the backstop after a sync: a person
 * tombstone from another device deletes the person here without touching the
 * rows that named them, so attribution has to be recomputed rather than trusted.
 *
 * Cards whose own owner is missing are reported, never guessed at — that is the
 * one case no rule can settle.
 */
export async function reconcilePersonAttribution(options?: { markDirtyOnChange?: boolean }): Promise<AttributionRepair> {
  return options?.markDirtyOnChange === false
    ? reconcilePersonAttributionInternal(options)
    : localMutation(() => reconcilePersonAttributionInternal(options));
}

async function reconcilePersonAttributionInternal(
  options?: { markDirtyOnChange?: boolean },
): Promise<AttributionRepair> {
  const [transactions, statements, cards, people] = await Promise.all([
    db.transactions.toArray(),
    db.statements.toArray(),
    db.cards.toArray(),
    db.people.toArray(),
  ]);

  const txDrift = findPersonDrift(transactions, cards);
  const stmtDrift = findPersonDrift(statements, cards);

  if (txDrift.length > 0 || stmtDrift.length > 0) {
    const now = new Date().toISOString();
    await db.transaction('rw', [db.transactions, db.statements], async () => {
      await db.transactions.bulkPut(
        txDrift.map(({ row, to }) => ({ ...row, personId: to, updatedAt: now })),
      );
      await db.statements.bulkPut(stmtDrift.map(({ row, to }) => ({ ...row, personId: to })));
    });
    if (options?.markDirtyOnChange !== false) await markDirty();
  }

  return {
    transactions: txDrift.length,
    statements: stmtDrift.length,
    orphaned: findOrphanedRows(transactions, cards).length,
    danglingCards: findDanglingCards(cards, people),
  };
}

export async function deleteCard(
  id: string,
  options?: { reassignToCardId?: string; deleteTransactions?: boolean },
): Promise<{ deleted: boolean; txCount: number }> {
  return localMutation(async () => {
    const txCount = await db.transactions.where('cardId').equals(id).count();

    if (options?.reassignToCardId) {
      const targetCard = await db.cards.get(options.reassignToCardId);
      if (targetCard) {
        const txs = await db.transactions.where('cardId').equals(id).toArray();
        await db.transactions.bulkPut(
          txs.map((tx) => ({
            ...tx,
            cardId: targetCard.id,
            personId: targetCard.personId,
            updatedAt: new Date().toISOString(),
          })),
        );
        await db.cards.delete(id);
        await recordTombstones('cards', [id]);

        return { deleted: true, txCount: 0 };
      }
    }

    if (options?.deleteTransactions) {
      const txs = await db.transactions.where('cardId').equals(id).toArray();
      await db.transactions.bulkDelete(txs.map((tx) => tx.id));
      await db.cards.delete(id);
      await recordTombstones('transactions', txs.map((tx) => tx.id));
      await recordTombstones('cards', [id]);

      return { deleted: true, txCount: 0 };
    }

    if (txCount > 0) return { deleted: false, txCount };
    await db.cards.delete(id);
    await recordTombstones('cards', [id]);

    return { deleted: true, txCount: 0 };
  });
}

export interface CleanupResult {
  removedDuplicates: number;
  /** Orphans still pointing at a card that no longer exists. Left untouched. */
  remainingOrphans: number;
}

/**
 * Clean up transactions whose cardId no longer exists in db.cards.
 *
 * An orphan that exactly duplicates a row on an active card (same reference, or
 * same date, amount, merchant and installment) is deleted — that is the residue
 * of a card being replaced, and the surviving copy is the one to keep.
 *
 * Everything else is **left alone and counted**. This used to reassign such rows
 * to `allCards[0]`, along with that card's `personId`: an arbitrary choice,
 * decided by primary-key order, that quietly moved one person's history onto
 * another. An orphan the reader can be told about beats a row filed under the
 * wrong name.
 *
 * `markDirtyOnChange: false` is for the call inside `applyMerged`: a sync writes
 * `dirty: false` when it finishes, so marking dirty mid-sync achieves nothing
 * except waking the auto-sync listener, which starts another sync, which runs
 * this cleanup again.
 */
export async function cleanupDuplicateAndOrphanedTransactions(options?: { markDirtyOnChange?: boolean }): Promise<CleanupResult> {
  return options?.markDirtyOnChange === false
    ? cleanupDuplicateAndOrphanedTransactionsInternal(options)
    : localMutation(() => cleanupDuplicateAndOrphanedTransactionsInternal(options));
}

async function cleanupDuplicateAndOrphanedTransactionsInternal(
  options?: { markDirtyOnChange?: boolean },
): Promise<CleanupResult> {
  const [allTxs, allCards, allStmts] = await Promise.all([
    db.transactions.toArray(),
    db.cards.toArray(),
    db.statements.toArray(),
  ]);

  if (allTxs.length === 0 && allStmts.length === 0) {
    return { removedDuplicates: 0, remainingOrphans: 0 };
  }

  const validCardIds = new Set(allCards.map((c) => c.id));
  const activeTxs = allTxs.filter((tx) => validCardIds.has(tx.cardId));
  const orphanedTxs = allTxs.filter((tx) => !validCardIds.has(tx.cardId));

  const toRemoveIds: string[] = [];

  for (const orphan of orphanedTxs) {
    const duplicate = activeTxs.find((active) => {
      if (orphan.reference && active.reference) {
        return orphan.reference === active.reference;
      }
      return (
        orphan.transactionDate === active.transactionDate &&
        Math.abs(orphan.amountILS - active.amountILS) < 0.001 &&
        orphan.merchantNormalized === active.merchantNormalized &&
        (orphan.installment?.current ?? 0) === (active.installment?.current ?? 0) &&
        (orphan.installment?.total ?? 0) === (active.installment?.total ?? 0)
      );
    });

    if (duplicate) {
      toRemoveIds.push(orphan.id);
    }
  }

  if (toRemoveIds.length > 0) {
    await recordTombstones('transactions', toRemoveIds);
    await db.transactions.bulkDelete(toRemoveIds);
  }

  // An orphaned statement with no surviving rows is residue and goes. One that
  // still has rows is left as it is, for the same reason its rows are.
  const remainingTxs = await db.transactions.toArray();
  const txCountByFileId = new Map<string, number>();
  for (const tx of remainingTxs) {
    txCountByFileId.set(tx.sourceFileId, (txCountByFileId.get(tx.sourceFileId) ?? 0) + 1);
  }

  const stmtsToRemove = allStmts
    .filter((stmt) => !validCardIds.has(stmt.cardId) && (txCountByFileId.get(stmt.id) ?? 0) === 0)
    .map((stmt) => stmt.id);

  if (stmtsToRemove.length > 0) {
    await db.statements.bulkDelete(stmtsToRemove);
  }

  if ((toRemoveIds.length > 0 || stmtsToRemove.length > 0) && (options?.markDirtyOnChange ?? true)) {
    await markDirty();
  }

  return {
    removedDuplicates: toRemoveIds.length,
    remainingOrphans: orphanedTxs.length - toRemoveIds.length,
  };
}
